import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/oetdot0e_SalesAssets%20Logo.png";

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password);
      toast.success('Account created! Create your first asset.');
      navigate('/create');
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* Back Button */}
      <div className="p-4">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0a2540] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="SalesAssets" 
                className="h-12 w-12 rounded-xl object-cover"
              />
              <span className="font-bold text-xl text-[#0a2540]">SalesAssets</span>
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <h1 className="text-2xl font-bold text-[#0a2540] mb-2 text-center">
              Create your account
            </h1>
            <p className="text-slate-500 text-center mb-8">
              Get your first sales asset free
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[#f8f9fa] border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540]/20"
                  data-testid="signup-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 bg-[#f8f9fa] border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540]/20 pr-12"
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#d4af37] hover:bg-[#c5a028] text-[#0a2540] font-bold"
                data-testid="signup-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            {/* Benefits */}
            <div className="mt-6 pt-6 border-t border-slate-100">
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  First sales asset completely free
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  No credit card required
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  £9/month after free trial
                </li>
              </ul>
            </div>

            <p className="text-center text-slate-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0a2540] font-medium hover:underline">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
